import { datadogRum } from '@datadog/browser-rum';
import { cond, equals } from 'ramda';
import React, { FC, ReactNode, forwardRef, useCallback, useEffect, useState } from 'react';
import Dropzone, { DropzoneRef, useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import BodyText from '@ui/atoms/BodyText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';

import { exists } from '@utils/general';

import { DropzoneErrors } from '@folx-types/error';

import Close from '@assets/vectors/close.inline.svg';
import Selfie from '@assets/vectors/selfie-upload.inline.svg';
import Upload from '@assets/vectors/upload.inline.svg';

import theme from '@theme/FolxTheme';

const DEFAULT_TEXT = 'Drop your image here or Browse';
const SUPPORT_TEXT = '(Supports: JPG, PNG, PDF)';

const DropInput = styled.input``;

interface IDropzoneProps {
	acceptedImage?: any;
	acceptedName?: string;
	acceptedTypes?: string[];
	acceptImageHandler?: (image: any, file: File) => void;
	cancelImageHandler?: () => void;
	errorImageHandler?: (error: DropzoneErrors | '') => void;
	error?: boolean;
	className?: string;
	imageType?: 'Selfie' | 'Photo ID' | 'Labs';
}

const Container = styled(FlexColumn)<{ hasFile: boolean; error: boolean }>`
	align-items: center;
	background-color: ${theme.colors.lightGray};
	border: ${({ hasFile, error }) =>
		(error && !hasFile && `0.2rem dashed ${theme.tints.darken40(theme.colors.red)}`) ||
		(!hasFile && `0.2rem dashed ${theme.colors.darkGray}`)};
	box-sizing: border-box;
	flex-shrink: 0;
	justify-content: center;
	overflow: hidden;
`;

const SelfieContainer = styled(Container)`
	border-radius: 50%;
	height: 15rem;
	width: 15rem;
`;

const PhotoIdContainer = styled(Container)`
	height: 8rem;
	width: 10.5rem;
	background-color: ${theme.colors.white};

	img {
		border-right: solid 1px ${theme.colors.black};
	}

	${BasicText} {
		overflow-wrap: anywhere;
		padding: 0 ${theme.spacing.sm};
		text-align: center;
	}
`;

const LabsContainer = styled(Container)`
	padding: ${theme.spacing.lg};
	width: 100%;
	max-width: 80rem;

	@media ${theme.responsive.breakpoints.tablet} {
		padding: ${theme.spacing.xxl};
	}
`;

const FileContainer = styled.div`
	align-items: center;
	background-color: ${theme.colors.lightGray};
	display: flex;
	width: 100%;
	max-width: 80rem;
`;

const TextContainer = styled.div`
	display: flex;
	padding-left: ${theme.spacing.md};
	align-items: center;
	background-color: ${theme.colors.white};
	height: 8rem;

	${BasicText} {
		text-align: center;
		overflow-wrap: anywhere;
	}
`;

const UploadedImage = styled.img`
	height: 8rem;
	object-fit: cover;
	width: 8rem;
`;

const FilenameContainer = styled.div`
	flex-grow: 1;
	overflow: auto;
	padding: 0 ${theme.spacing.sm};
`;

const FileName = styled(BodyText)`
	text-align: start;
	overflow-wrap: anywhere;
`;

const CancelButton = styled(Button)`
	background-color: ${theme.colors.transparent};
	height: 100%;
	padding: ${theme.spacing.lg};
	:hover {
		background-color: ${theme.colors.transparent};
	}
`;

const Image = styled.img`
	height: 100%;
	width: 100%;
	object-fit: cover;
`;

const DropzoneInput = forwardRef<ref, IDropzoneProps>(
	(
		{
			acceptedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif'],
			acceptImageHandler = () => {},
			cancelImageHandler = () => {},
			errorImageHandler = () => {},
			error = false,
			className,
			acceptedImage,
			acceptedName = '',
			imageType = 'Selfie',
		},
		dropzoneRef
	) => {
		const [uploadedFile, setUploadedFile] = useState<File | null>(null);
		const [isPdf, setIsPdf] = useState(false);

		const onDrop = useCallback((acceptedImages: File[], fileRejections: File[]) => {
			if (fileRejections && fileRejections.length) {
				datadogRum.addError('File type(s) not supported', {
					files: fileRejections.map((file) => ({
						name: file?.name,
						type: file?.type,
						size: file?.size,
					})),
				});
			}

			acceptedImages.forEach((file: File) => {
				const reader = new FileReader();

				reader.onabort = () => {
					datadogRum.addError('File reading was aborted', {
						type: file?.type,
						name: file?.name,
						size: file?.size,
					});
				};
				reader.onerror = () => {
					datadogRum.addError('File reading failed', {
						type: file?.type,
						name: file?.name,
						size: file?.size,
					});
				};
				reader.onload = () => {
					// Use our processor
					acceptImageHandler(reader.result, file);
					setUploadedFile(file);
				};

				reader.readAsDataURL(file);
			});
		}, []);

		const hasFile = !!acceptedImage;

		useEffect(() => {
			!hasFile && setUploadedFile(null);
		}, [hasFile]);

		useEffect(() => {
			if (!acceptedImage) {
				return;
			}
			const getBlob = async () => {
				return await (await fetch(acceptedImage)).blob();
			};

			getBlob().then((blob) => {
				return setIsPdf(blob.type === 'application/pdf');
			});
		}, [acceptedImage]);

		const Component = cond<string, ReactNode>([
			[equals('Selfie'), () => SelfieContainer],
			[equals('Photo ID'), () => (isPdf ? TextContainer : PhotoIdContainer)],
			[equals('Labs'), () => (hasFile ? FileContainer : LabsContainer)],
		])(imageType);

		const getUpload = () =>
			imageType === 'Selfie' ? (
				<Selfie height="50%" width="50%" alt="Headshot Upload" />
			) : (
				<Upload height={theme.spacing.xxl} width={theme.spacing.xxl} alt="Cloud Upload" />
			);

		const getUploadedContent = () =>
			cond<string, ReactNode>([
				[equals('Selfie'), () => <Image src={acceptedImage} alt="headshot" />],
				[
					equals('Photo ID'),
					() =>
						isPdf ? (
							<BasicText>{exists(acceptedName) ? acceptedName : 'PDF Upload'}</BasicText>
						) : (
							<Image src={acceptedImage} alt="identification" />
						),
				],
				[
					equals('Labs'),
					() => (
						<>
							{(!uploadedFile || !(uploadedFile?.type === 'application/pdf')) && (
								<UploadedImage alt="headshot" src={acceptedImage} />
							)}
							<FilenameContainer>
								<FileName size="sm">{uploadedFile?.name ?? imageType}</FileName>
							</FilenameContainer>
							<CancelButton
								label={<Close />}
								onClick={(event) => {
									cancelImageHandler();
									setUploadedFile(null);
									event.stopPropagation();
								}}
							/>
						</>
					),
				],
			])(imageType);

		return (
			<Dropzone
				ref={dropzoneRef}
				accept={acceptedTypes.join(', ')}
				maxFiles={1}
				onDrop={onDrop}
				onDropRejected={() => errorImageHandler('invalid')}>
				{({ getRootProps, getInputProps }) => (
					<Component hasFile={hasFile} error={error} {...getRootProps()} className={className}>
						<DropInput {...getInputProps()} aria-label="Upload Photo" />
						{hasFile ? getUploadedContent() : getUpload()}
					</Component>
				)}
			</Dropzone>
		);
	}
);

DropzoneInput.displayName = 'DropzoneInput';
export default styled(DropzoneInput)``;
